import { useMemo, useRef, useState } from "react";

import {
  DocumentMinusIcon,
  PresentationChartLineIcon,
} from "@heroicons/react/24/solid";

import * as XLSX from "xlsx";

import AddButton from "../../../components/AddButton";
import ConfirmBoxDelete from "../../../components/ConfirmBoxDelete";
import DeactivateButton from "../../../components/DeactivateButton";
import DownloadButton from "../../../components/DownloadButton";

import CustomPaginatedTable from "../../../components/tables/CustomPaginatedTable/Table";

import type { PaginatedTableQuery } from "../../../components/tables/CustomPaginatedTable/types";
import { getDigitalAssetColumns } from "./DigitalAssetTableColumns";

import { toast } from "../../../components/toastNotification";

import type {
  DigitalAsset,
  DigitalAssetDownloadRow,
  DigitalAssetPermissions,
} from "./types";

import { EMPTY_DIGITAL_ASSET_PERMISSIONS } from "./types";


interface DigitalAssetTableProps {
  assets: DigitalAsset[];
  totalCount: number;
  query: PaginatedTableQuery;
  selectedRows: DigitalAsset[];

  permissions?: DigitalAssetPermissions;

  viewFromOffice?: boolean;

  isLoading: boolean;
  isDownloading: boolean;

  onQueryChange: (query: PaginatedTableQuery) => void;

  onSelectionChange: (rows: DigitalAsset[]) => void;

  onDownload: () => Promise<DigitalAssetDownloadRow[]>;

  onAdd: () => void;

  onView: (asset: DigitalAsset) => void;

  onEdit: (asset: DigitalAsset) => void;

  onDeactivate: (ids: number[]) => void;

  onOpenOffice?: (officeCode: string) => void;
}

const formatDate = (value?: string | null): string => {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toLocaleDateString();
};

const DigitalAssetTable = ({
  assets,
  totalCount,
  query,
  selectedRows,
  permissions = EMPTY_DIGITAL_ASSET_PERMISSIONS,
  viewFromOffice = false,
  isLoading,
  isDownloading,
  onQueryChange,
  onSelectionChange,
  onDownload,
  onAdd,
  onView,
  onEdit,
  onDeactivate,
  onOpenOffice,
}: DigitalAssetTableProps) => {
  const [showConfirmation, setShowConfirmation] = useState(false);

  const [deleteIds, setDeleteIds] = useState<number[]>([]);

  const cancelButtonRef = useRef<HTMLButtonElement>(null);

  const requestDeactivation = () => {
    const ids = selectedRows
      .filter(({ isActive }) => isActive)
      .map(({ id }) => id);

    if (ids.length === 0) {
      return;
    }

    setDeleteIds(ids);
    setShowConfirmation(true);
  };

  const exportDigitalAssets = async () => {
    try {
      const data = await onDownload();

      const rows = data.map((asset) => ({
        Id: asset.digitalAssetId,

        Name: asset.digitalAssetName,

        OfficeId: asset.officeId ?? "",

        OfficeCode: asset.officeCode ?? "",

        OfficeName: asset.officeName ?? "",

        StoreNumber: asset.storeNumber ?? "",

        CountryCode: asset.countryCode ?? "",

        OfficeStatus: asset.officeStatus ?? "",

        Agency: asset.agency ?? "",

        Digital_Tracking_Number__c: asset.digitalTrackingNumber ?? "",

        Website__c: asset.website ?? "",

        Directory_General_Description__c:
          asset.directoryGeneralDescription ?? "",

        Directory_Company_Story__c:
          asset.directoryCompanyStory ?? asset.directory_Company_Story__C ?? "",

        Facebook_Company_Overview__c: asset.facebookCompanyOverview ?? "",

        Facebook_Username_Page_URL__c: asset.facebookUsernamePageUrl ?? "",

        Google_Business_URL__c: asset.googleBusinessUrl ?? "",

        Google_Description__c: asset.googleDescription ?? "",

        Meta_Description__c: asset.metaDescription ?? "",

        Meta_Description_SPA__c: asset.metaDescriptionSpa ?? "",

        Online_Directories_Brand_Name__c:
          asset.onlineDirectoriesBrandaName ?? "",

        Page_Description__c: asset.pageDescription ?? "",

        Page_Description_SPA__c: asset.pageDescriptionSpa ?? "",

        Page_Title__c: asset.pageTitle ?? "",

        Page_Title_SPA__c: asset.pageTitleSpa ?? "",

        Title_Tag__c: asset.titleTag ?? "",

        Title_Tag_SPA__c: asset.titleTagSpa ?? "",

        Website_Local_Profile_URL__c: asset.websiteLocalProfileUrl ?? "",

        Website_Local_Profile_URL_SPA__c: asset.websiteLocalProfileUrlSpa ?? "",

        Yelp_Description_Specialties__c: asset.yelpDescriptionSpecialties ?? "",

        Yelp_Profile_URL__c: asset.yelpProfileUrl ?? "",

        Twitter_URL: asset.twitterUrl ?? "",

        LinkedIn_URL: asset.linkedInUrl ?? "",

        Youtube_URL: asset.youtubeUrl ?? "",

        Instagram_URL: asset.instagramUrl ?? "",

        Apple_URL: asset.appleUrl ?? "",

        AppleDescription: asset.appleDescription ?? "",

        Category: asset.appleCategoryName ?? "",
        AppName: asset.appleAppName ?? "",
        QuickLink: asset.appleQuickLink ?? "",

        Bing: asset.bing ?? "",

        BingDescription: asset.bingDescription ?? "",

        Short_Description: asset.shortDescription ?? "",

        CreatedBy: asset.createdBy ?? "",

        CreatedOn: formatDate(asset.createdOn),

        UpdatedBy: asset.updatedBy ?? "",

        UpdatedOn: formatDate(asset.updatedOn),

        ServiceName: asset.serviceName ?? "",

        LastSyncDate: formatDate(asset.lastSyncDate),

        IsSync: asset.isSync ?? "",

        Last_Modified_Date: formatDate(asset.lastModifiedDate),

        IsActive: asset.isActive ?? "",
      }));

      const workbook = XLSX.utils.book_new();

      const downloadedFile = XLSX.utils.json_to_sheet(rows);

      const dateTimeToday = new Date().toISOString().split("T")[0];

      XLSX.utils.book_append_sheet(
        workbook,
        downloadedFile,
        "DigitalAssets_" + dateTimeToday,
      );

      XLSX.writeFile(workbook, "DigitalAssets_" + dateTimeToday + ".xlsx");

      toast.fire(
        "Download Digital Assets",
        "Successfully downloaded digital assets.",
        "success",
      );
    } catch {
      // API error toast is handled
      // by the mutation in index.tsx.
    }
  };

  const columns = useMemo(
    () =>
      getDigitalAssetColumns({
        canEdit: permissions.canEdit,
        canViewOffice: permissions.canViewOffice,
        hasSelectedRows: selectedRows.length > 0,
        onView,
        onEdit,
        onOpenOffice,
      }),
    [
      onEdit,
      onOpenOffice,
      onView,
      permissions.canEdit,
      permissions.canViewOffice,
      selectedRows.length,
    ],
  );

  const selectedActionBar =
    permissions.canDeactivate && selectedRows.length > 0 ? (
      <div className="flex justify-start mr-2 pb-2">
        <DeactivateButton onClickFunction={requestDeactivation}>
          <>
            <DocumentMinusIcon className="h-4 w-4 font-bold" />
            <span>Deactivate</span>
          </>
        </DeactivateButton>

        <div className="mt-1 ml-2 text-xs text-primary">
          Deactivate Selected Row/s
          <p className="inline-block font-bold">
            &nbsp;(
            {selectedRows.length})
          </p>
        </div>
      </div>
    ) : null;

  const showTopActions =
    !viewFromOffice ||
    (viewFromOffice && !isLoading && totalCount < 1 && permissions.canAdd);

  return (
    <section
      className="px-8 py-4 min-h-fit"
      aria-labelledby="digital-assets-title"
    >
      {!viewFromOffice ? (
        <div className="flex gap-2 items-center">
          <PresentationChartLineIcon className="text-white bg-blue-500 rounded-md h-7 w-7 p-1" />

          <div className="flex flex-col leading-tight">
            <h3 className="text-xs font-medium text-gray-500">
              Digital Assets Module
            </h3>

            <h1
              id="digital-assets-title"
              className="mr-auto font-bold text-primary text-md uppercase"
            >
              All Digital Assets
            </h1>
          </div>
        </div>
      ) : null}

      <div className="flex flex-row gap-2 justify-between items-center">
        {!viewFromOffice && permissions.canDeactivate && (
          <div className="text-secondary italic text-xs3 my-2">
            <b>Note: </b>
            Tick the box to deactivate some data.
          </div>
        )}
        {showTopActions && (
          <div className="gap-2 flex items-center">
            {permissions.canAdd && (
              <AddButton
                buttonText="Add"
                className={
                  selectedRows.length > 0 ? "opacity-50" : "opacity-100"
                }
                disabled={selectedRows.length > 0}
                onClick={onAdd}
              />
            )}

            {permissions.canDownload && (
              <div className="box-content">
                <DownloadButton
                  disabled={isLoading || isDownloading || totalCount === 0}
                  buttonValue={
                    isDownloading ? "Downloading..." : "Download File"
                  }
                  onClick={() => {
                    void exportDigitalAssets();
                  }}
                />
              </div>
            )}
          </div>
        )}
      </div>

      <main className="w-full mt-2 bg-white overflow-x-auto box-shadow">
        <CustomPaginatedTable
          columns={columns}
          data={assets}
          totalCount={totalCount}
          query={query}
          onQueryChange={onQueryChange}
          rowKey={(asset) => asset.id}
          isLoading={isLoading}
          search={true}
          searchPlaceholder="Search"
          selection={permissions.canDeactivate}
          selectedRows={selectedRows}
          onSelectionChange={onSelectionChange}
          isRowSelectable={(asset) => asset.isActive === true}
          selectedActionBar={selectedActionBar}
          emptyMessage="No records to display."
          pageSizeOptions={[10, 15, 20, 30, 100]}
        />
      </main>

      <ConfirmBoxDelete
        ModalTitle="Deactivate Digital Assets"
        qConfirmation={
          <div className="text-gray-500 font-normal">
            Are you sure you want to deactivate {deleteIds.length}{" "}
            {deleteIds.length === 1 ? "Digital Asset" : "Digital Assets"}?
          </div>
        }
        cancelButtonRef={cancelButtonRef}
        isOpen={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        onClickFunction={() => {
          onDeactivate(deleteIds);

          setShowConfirmation(false);
        }}
      />
    </section>
  );
};

export default DigitalAssetTable;
